import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { NavLink, To } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

interface MainSliderProps {
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  visibleSlides: {
    image: string;
    title: string;
    description: string;
    url?: string;
  }[];
  handleNext: () => void;
  handlePrev: () => void;
}

export default function MainSlider({ visibleSlides }: MainSliderProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0); // 현재 슬라이드 인덱스
  const [isPlaying, setIsPlaying] = useState<boolean>(true); // 재생 여부
  const progressBarRef = useRef<HTMLDivElement>(null); // 진행 바 참조
  const SLIDE_DURATION = 5000; // 각 슬라이드의 지속 시간 (밀리초)

  // 사이드바 상태를 전역에서 가져오도록 수정
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const checkSidebarState = () => {
      // 사이드바 요소를 직접 선택
      const sidebar = document.querySelector('.fixed.left-0.top-0.h-screen');
      if (sidebar) {
        // getComputedStyle을 사용하여 실제 적용된 너비를 가져옴
        const sidebarStyle = window.getComputedStyle(sidebar);
        const sidebarWidth = parseFloat(sidebarStyle.width);
        setIsSidebarCollapsed(sidebarWidth <= 60);
      }
    };

    // 초기 상태 체크
    checkSidebarState();

    // MutationObserver를 사용하여 DOM 변화 감지
    const observer = new MutationObserver(checkSidebarState);
    const sidebar = document.querySelector('.fixed.left-0.top-0.h-screen');
    
    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // 윈도우 리사이즈 이벤트에도 반응
    window.addEventListener('resize', checkSidebarState);

    // 트랜지션이 완료된 후에도 체크
    document.addEventListener('transitionend', checkSidebarState);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkSidebarState);
      document.removeEventListener('transitionend', checkSidebarState);
    };
  }, []);

  // 다음 슬라이드로 이동하는 함수
  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === visibleSlides.length - 1 ? 0 : prevIndex + 1
    );
  };

  // 이전 슬라이드로 이동하는 함수
  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? visibleSlides.length - 1 : prevIndex - 1
    );
  };

  // 슬라이드 자동 재생 타이머 설정
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        handleNext();
      }, SLIDE_DURATION); // SLIDE_DURATION마다 슬라이드 변경

      return () => clearInterval(interval); // 컴포넌트 언마운트 시 타이머 해제
    }
  }, [isPlaying, currentIndex]);

  // 슬라이드 변경 시 진행바 애니메이션 재설정
  useEffect(() => {
    if (progressBarRef.current) {
      progressBarRef.current.style.transition = 'none'; // 애니메이션 초기화
      progressBarRef.current.style.width = '0%'; // 진행 바 리셋

      // 가속/감속 애니메이션 시
      setTimeout(() => {
        if (progressBarRef.current) {
          progressBarRef.current.style.transition = `width ${SLIDE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
          progressBarRef.current.style.width = '100%'; // 진행 바 채우기
        }
      }, 100); // 애니메이션 지연 시간
    }
  }, [currentIndex]);

  return (
    <div className="relative w-full overflow-hidden">
      <main 
        className="relative h-[680px] mobile:h-[300px] transition-all duration-300"
        style={{
          width: isSidebarCollapsed ? 'calc(100vw - 60px)' : 'calc(100vw - 250px)',
          marginLeft: isSidebarCollapsed ? '60px' : '250px',
          transform: 'translateX(0)',  // 트랜지션을 부드럽게 만들기 위해 추가
        }}
      >
        <div
          className="absolute inset-0 mobile:mt-[80px]"
          style={{
            backgroundImage: `url(${visibleSlides[currentIndex].image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: 'all 0.5s ease-in-out',
            imageRendering: 'pixelated',
            height: window.innerWidth <= 767 ? '300px' : '100%',
          }}
        >
          <div className="absolute bottom-[120px] left-[80px] flex flex-col justify-end mobile:hidden">
            <h2 className="text-[40px] text-white Pretendard-Bold uppercase tracking-wide">
              {visibleSlides[currentIndex].title}
            </h2>
            <p className="mt-4 text-lg text-white/90 leading-relaxed tracking-wide Pretendard-Regular max-w-[600px]">
              {visibleSlides[currentIndex].description}
            </p>
            {visibleSlides[currentIndex].url && (
              <NavLink 
                to={visibleSlides[currentIndex].url || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-8"
              >
                <button className="bg-[#6C48F9] hover:bg-[#5636D6] transition-colors duration-300 text-white px-8 py-3 rounded-lg text-base Pretendard-Regular">
                  바로보기
                </button>
              </NavLink>
            )}
          </div>
        </div>
      </main>

      {/* 썸네일과 컨트롤 버튼을 메인 슬라이더 밖으로 이동 */}
      <div className="absolute bottom-7 left-[calc(70%+400px)] transform -translate-x-1/2 mobile:justify-center z-50">
        <div className="flex flex-col items-end gap-6 mobile:hidden">
          {/* 썸네일 미리보기 */}
          <div className="flex items-center gap-4">
            {visibleSlides.map((slide, index) => (
              <div
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative cursor-pointer transition-all duration-300 ${
                  currentIndex === index ? 'border-2 border-white' : 'border-2 border-transparent'
                }`}
              >
                <img 
                  src={slide.image} 
                  alt={slide.title}
                  className="w-[120px] h-[67px] object-cover"
                />
                {currentIndex === index && (
                  <div className="absolute inset-0 bg-black/20"></div>
                )}
              </div>
            ))}
          </div>

          {/* 컨트롤 버튼 */}
          <div className="flex items-center gap-6">
            <button onClick={handlePrev}>
              <IoIosArrowBack size={20} className="text-white/90" />
            </button>
            <span className="text-white/90 text-sm tracking-[0.2em] font-normal min-w-[50px] text-center">
              {currentIndex + 1} / {visibleSlides.length}
            </span>
            <button onClick={() => setIsPlaying((prev) => !prev)}>
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="7" y="6" width="1.5" height="12" fill="white" />
                  <rect x="14" y="6" width="1.5" height="12" fill="white" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M8 5V19L19 12L8 5Z" fill="white" />
                </svg>
              )}
            </button>
            <button onClick={handleNext}>
              <IoIosArrowForward size={20} className="text-white/90" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
